// Every time on the site is shown, and entered, in South African Standard Time.
// SAST is UTC+2 all year (no daylight saving), which keeps conversion exact.

const TZ = "Africa/Johannesburg";

const fmt = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Date(iso).toLocaleString("en-ZA", { timeZone: TZ, ...options });

/** "Thursday, 22 October 2026" */
export const formatLongDate = (iso: string) => fmt(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** "22 Oct 2026" */
export const formatShortDate = (iso: string) => fmt(iso, { day: "numeric", month: "short", year: "numeric" });

/** "18:00" */
export const formatTime = (iso: string) => fmt(iso, { hour: "2-digit", minute: "2-digit", hour12: false });

/** "22 Oct 2026, 18:00" */
export const formatDateTime = (iso: string) => `${formatShortDate(iso)}, ${formatTime(iso)}`;

/** "18:00–19:30" */
export const formatTimeRange = (startAt: string, endAt: string) => `${formatTime(startAt)}–${formatTime(endAt)}`;

export const durationMinutes = (startAt: string, endAt: string) =>
  Math.max(0, Math.round((Date.parse(endAt) - Date.parse(startAt)) / 60000));

/** Pieces for the calendar-style date block on cards */
export const dateBlock = (iso: string) => ({
  month: fmt(iso, { month: "short" }),
  day: fmt(iso, { day: "numeric" }),
  weekday: fmt(iso, { weekday: "short" }),
});

/** ISO timestamp → the SAST values a date and a time input expect */
export function toSastInputs(iso?: string): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
}

/** SAST date ("2026-10-22") and time ("18:00") → ISO timestamp in UTC */
export const fromSastInputs = (date: string, time: string) => new Date(`${date}T${time}:00+02:00`).toISOString();

/** Initials skip titles like Dr or Prof and use up to two words: "Dr Naledi Mahlangu" → "NM" */
export const initials = (name: string) =>
  name
    .replace(/^(Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join("");

/** "Medical devices: getting started!" → "medical-devices-getting-started" */
export const slugify = (text: string) =>
  text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "webinar";

/** "in 17 days", "in 5 h 12 min", "in 40 min", "in under a minute" */
export function formatTimeUntil(iso: string, now = Date.now()) {
  const minutes = Math.max(0, Math.ceil((Date.parse(iso) - now) / 60000));
  if (minutes < 1) return "in under a minute";
  if (minutes >= 48 * 60) return `in ${Math.round(minutes / (24 * 60))} days`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `in ${[hours ? `${hours} h` : "", rest ? `${rest} min` : ""].filter(Boolean).join(" ")}`;
}

/** "02:00 SAST today", "02:00 SAST tomorrow", or further ahead "1 November, 02:00 SAST" */
export function formatResetTime(iso: string, now = Date.now()) {
  const day = (time: string | number) => new Date(time).toLocaleDateString("en-CA", { timeZone: TZ });
  if (day(iso) === day(now)) return `${formatTime(iso)} SAST today`;
  if (day(iso) === day(now + 864e5)) return `${formatTime(iso)} SAST tomorrow`;
  return `${fmt(iso, { day: "numeric", month: "long" })}, ${formatTime(iso)} SAST`;
}
